/**
 * Quality Report Drawer
 * Create / Edit quality reports with dynamic checklist items.
 */

import { Plus, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DatePicker, Dropdown, Input, SearchableDropdown, TextArea } from '../forms';
import { useToast } from '../../contexts/ToastContext';
import {
  useCreateQualityReportMutation,
  useUpdateQualityReportMutation,
} from '../../services/qualityReportApi';
import type {
  QualityReport,
  QualityCheckItem,
  QualityCheckItemStatus,
  QualityReportCategory,
  QualityReportPriority,
  QualityReportType,
  CreateQualityReportRequest,
  UpdateQualityReportRequest,
} from '../../types/qualityReport';

// ============================================================================
// Constants
// ============================================================================

const REPORT_TYPE_OPTIONS = [
  { value: 'inspection', label: 'Inspection' },
  { value: 'ncr', label: 'NCR (Non-Conformance Report)' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'audit', label: 'Audit' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In Progress' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const CATEGORY_OPTIONS = [
  { value: 'structural', label: 'Structural' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'finishes', label: 'Finishes' },
  { value: 'sitework', label: 'Sitework' },
  { value: 'fire_safety', label: 'Fire Safety' },
  { value: 'general', label: 'General' },
];

const ITEM_STATUS_OPTIONS = [
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
  { value: 'na', label: 'N/A' },
  { value: 'needs_review', label: 'Needs Review' },
];

const DEFAULT_ITEM_CATEGORIES = [
  'Structural Integrity',
  'Electrical Systems',
  'Plumbing',
  'HVAC',
  'Finishes',
  'Safety',
  'Documentation',
  'General',
];

// ============================================================================
// Props
// ============================================================================

interface QualityReportDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  report?: QualityReport | null;
  mode: 'create' | 'edit';
}

// ============================================================================
// Helpers
// ============================================================================

const getItemStatusColor = (status: QualityCheckItemStatus): string => {
  switch (status) {
    case 'pass':
      return 'bg-green-100 text-green-700 border-green-300';
    case 'fail':
      return 'bg-red-100 text-red-700 border-red-300';
    case 'needs_review':
      return 'bg-orange-100 text-orange-700 border-orange-300';
    case 'na':
    default:
      return 'bg-gray-100 text-gray-600 border-gray-300';
  }
};

const buildEmptyItem = (): Omit<QualityCheckItem, '_id'> => ({
  category: '',
  item: '',
  status: 'na',
  notes: '',
  correctiveAction: '',
  photos: [],
});

// ============================================================================
// Component
// ============================================================================

const QualityReportDrawer: React.FC<QualityReportDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  report,
  mode,
}) => {
  const toast = useToast();

  // Form state
  const [reportType, setReportType] = useState<QualityReportType>('inspection');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [area, setArea] = useState('');
  const [inspectorName, setInspectorName] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [status, setStatus] = useState<'draft' | 'in_progress'>('draft');
  const [priority, setPriority] = useState<QualityReportPriority>('medium');
  const [category, setCategory] = useState<QualityReportCategory>('general');
  const [items, setItems] = useState<Omit<QualityCheckItem, '_id'>[]>([]);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createReport, { isLoading: isCreating }] = useCreateQualityReportMutation();
  const [updateReport, { isLoading: isUpdating }] = useUpdateQualityReportMutation();

  const isSubmitting = isCreating || isUpdating;

  // Reset / populate form when drawer opens
  useEffect(() => {
    if (isOpen && mode === 'edit' && report) {
      setReportType(report.reportType);
      setTitle(report.title || '');
      setDescription(report.description || '');
      setLocation(report.location || '');
      setArea(report.area || '');
      setInspectorName(report.inspectorName || '');
      setScheduledDate(report.scheduledDate ? report.scheduledDate.split('T')[0] : '');
      setStatus((report.status === 'draft' || report.status === 'in_progress') ? report.status : 'draft');
      setPriority(report.priority);
      setCategory(report.category);
      setNotes(report.notes || '');
      setItems(
        report.items.map(i => ({
          category: i.category,
          item: i.item,
          status: i.status,
          notes: i.notes || '',
          correctiveAction: i.correctiveAction || '',
          photos: i.photos || [],
        }))
      );
    } else if (isOpen && mode === 'create') {
      setReportType('inspection');
      setTitle('');
      setDescription('');
      setLocation('');
      setArea('');
      setInspectorName('');
      setScheduledDate(new Date().toISOString().split('T')[0]);
      setStatus('draft');
      setPriority('medium');
      setCategory('general');
      setNotes('');
      setItems([]);
      setErrors({});
    }
  }, [isOpen, mode, report]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!title.trim()) errs.title = 'Title is required';
    if (!reportType) errs.reportType = 'Report type is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ============================================================================
  // Item management
  // ============================================================================

  const handleAddItem = useCallback(() => {
    setItems(prev => [...prev, buildEmptyItem()]);
  }, []);

  const handleRemoveItem = useCallback((index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleItemChange = useCallback(
    (index: number, field: keyof Omit<QualityCheckItem, '_id'>, value: string) => {
      setItems(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    },
    []
  );

  const handleAddTemplateItems = useCallback((categoryName: string) => {
    const defaults: Omit<QualityCheckItem, '_id'>[] = [
      { category: categoryName, item: 'Work meets design specifications', status: 'na' },
      { category: categoryName, item: 'Materials are approved and as specified', status: 'na' },
      { category: categoryName, item: 'Installation per manufacturer requirements', status: 'na' },
    ];
    setItems(prev => [...prev, ...defaults]);
  }, []);

  // ============================================================================
  // Submit
  // ============================================================================

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const payload: CreateQualityReportRequest | UpdateQualityReportRequest = {
        reportType,
        title: title.trim(),
        description: description.trim() || undefined,
        location: location.trim() || undefined,
        area: area.trim() || undefined,
        inspectorName: inspectorName.trim() || undefined,
        scheduledDate: scheduledDate || undefined,
        status,
        priority,
        category,
        notes: notes.trim() || undefined,
        items: items.filter(i => i.item.trim() !== ''),
      };

      if (mode === 'create') {
        await createReport({
          projectId,
          data: payload as CreateQualityReportRequest,
        }).unwrap();
        toast.success('Quality report created successfully');
      } else if (report) {
        await updateReport({
          projectId,
          reportId: report._id,
          data: payload as UpdateQualityReportRequest,
        }).unwrap();
        toast.success('Quality report updated successfully');
      }

      onClose();
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'data' in err &&
        typeof (err as { data?: { message?: string } }).data?.message === 'string'
          ? (err as { data: { message: string } }).data.message
          : mode === 'create'
          ? 'Failed to create quality report'
          : 'Failed to update quality report';
      toast.error(message);
    }
  };

  if (!isOpen) return null;

  // Summarize item statuses
  const passCount = items.filter(i => i.status === 'pass').length;
  const failCount = items.filter(i => i.status === 'fail').length;
  const reviewCount = items.filter(i => i.status === 'needs_review').length;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer — large because it has multiple sections */}
      <div className="fixed inset-6 z-[100] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {mode === 'create' ? 'Create Quality Report' : 'Edit Quality Report'}
              </h2>
              {report && (
                <p className="text-xs text-gray-500 mt-0.5">{report.reportNumber}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="space-y-6">
              {/* ============================================================
                  SECTION: Basic Info
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Report Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Report Type */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Report Type <span className="text-red-500">*</span>
                    </label>
                    <SearchableDropdown
                      options={REPORT_TYPE_OPTIONS}
                      value={reportType}
                      onChange={val => setReportType(val as QualityReportType)}
                      placeholder="Select type"
                    />
                    {errors.reportType && (
                      <p className="text-xs text-red-500 mt-1">{errors.reportType}</p>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Category</label>
                    <SearchableDropdown
                      options={CATEGORY_OPTIONS}
                      value={category}
                      onChange={val => setCategory(val as QualityReportCategory)}
                      placeholder="Select category"
                    />
                  </div>

                  {/* Title */}
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder="Enter report title"
                    />
                    {errors.title && (
                      <p className="text-xs text-red-500 mt-1">{errors.title}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <TextArea
                      value={description}
                      onChange={setDescription}
                      placeholder="Describe the purpose or scope of this quality report"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* ============================================================
                  SECTION: Location & Inspector
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Location & Inspector
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Location</label>
                    <Input
                      value={location}
                      onChange={e => setLocation(e.target.value)}
                      placeholder="Building, floor, zone..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Area / Zone</label>
                    <Input
                      value={area}
                      onChange={e => setArea(e.target.value)}
                      placeholder="Specific area or zone"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Inspector Name</label>
                    <Input
                      value={inspectorName}
                      onChange={e => setInspectorName(e.target.value)}
                      placeholder="Inspector's name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Scheduled Date</label>
                    <DatePicker
                      value={scheduledDate}
                      onChange={setScheduledDate}
                      placeholder="Select date"
                    />
                  </div>
                </div>
              </div>

              {/* ============================================================
                  SECTION: Status & Priority
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Status & Priority
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Status</label>
                    <Dropdown
                      options={STATUS_OPTIONS}
                      value={status}
                      onChange={val => setStatus(val as 'draft' | 'in_progress')}
                      placeholder="Select status"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Priority</label>
                    <Dropdown
                      options={PRIORITY_OPTIONS}
                      value={priority}
                      onChange={val => setPriority(val as QualityReportPriority)}
                      placeholder="Select priority"
                    />
                  </div>
                </div>
              </div>

              {/* ============================================================
                  SECTION: Checklist Items
              ============================================================ */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Quality Checklist
                    </h3>
                    {items.length > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {items.length} items &mdash; {passCount} pass, {failCount} fail, {reviewCount} review
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Quick-add template */}
                    <div className="relative group">
                      <button
                        type="button"
                        className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 text-gray-600"
                      >
                        Add Template
                      </button>
                      <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[180px] hidden group-hover:block">
                        {DEFAULT_ITEM_CATEGORIES.map(cat => (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => handleAddTemplateItems(cat)}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 text-gray-700"
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddItem}
                      className="flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 font-medium"
                    >
                      <Plus className="h-4 w-4" />
                      Add Item
                    </button>
                  </div>
                </div>

                {items.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <p className="text-sm text-gray-500">No checklist items yet.</p>
                    <button
                      type="button"
                      onClick={handleAddItem}
                      className="mt-2 text-sm text-brand-600 hover:underline"
                    >
                      Add your first item
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.map((item, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-xl p-4 bg-gray-50"
                      >
                        <div className="flex items-start gap-3">
                          {/* Status badge */}
                          <div className="flex-shrink-0 mt-1">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getItemStatusColor(item.status)}`}
                            >
                              {item.status === 'needs_review' ? 'Review' : item.status.toUpperCase()}
                            </span>
                          </div>

                          {/* Fields */}
                          <div className="flex-1 grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Category
                              </label>
                              <Input
                                value={item.category}
                                onChange={e => handleItemChange(index, 'category', e.target.value)}
                                placeholder="Category"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Item / Description
                              </label>
                              <Input
                                value={item.item}
                                onChange={e => handleItemChange(index, 'item', e.target.value)}
                                placeholder="Describe the inspection item"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Status
                              </label>
                              <Dropdown
                                options={ITEM_STATUS_OPTIONS}
                                value={item.status}
                                onChange={val =>
                                  handleItemChange(index, 'status', val as QualityCheckItemStatus)
                                }
                                placeholder="Status"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Notes
                              </label>
                              <Input
                                value={item.notes || ''}
                                onChange={e => handleItemChange(index, 'notes', e.target.value)}
                                placeholder="Observation notes"
                              />
                            </div>
                            {(item.status === 'fail' || item.status === 'needs_review') && (
                              <div className="col-span-3">
                                <label className="block text-xs text-gray-500 mb-0.5">
                                  Corrective Action
                                </label>
                                <Input
                                  value={item.correctiveAction || ''}
                                  onChange={e =>
                                    handleItemChange(index, 'correctiveAction', e.target.value)
                                  }
                                  placeholder="Describe required corrective action"
                                />
                              </div>
                            )}
                          </div>

                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                            aria-label="Remove item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ============================================================
                  SECTION: Notes
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Notes
                </h3>
                <TextArea
                  value={notes}
                  onChange={setNotes}
                  placeholder="Additional notes, observations, or context..."
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              {items.length > 0
                ? `${items.filter(i => i.item.trim()).length} valid item(s)`
                : 'No items added'}
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting
                  ? mode === 'create'
                    ? 'Creating...'
                    : 'Saving...'
                  : mode === 'create'
                  ? 'Create Report'
                  : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default QualityReportDrawer;
